
import { useState, useCallback, useRef, useEffect } from 'react';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  prefix?: string;
  icon: string;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
}

/**
 * PropertySlider – a reusable numeric slider component.
 *
 * The component now includes defensive checks and clearer error handling
 * for mouse / touch interactions, ensuring robustness when the track
 * element is missing or when unexpected events occur.
 */
export function PropertySlider({
  label,
  value,
  min,
  max,
  step,
  suffix,
  prefix,
  icon,
  onChange,
  formatValue,
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Guard against division by zero (min === max)
  const safeRange = Math.max(max - min, 1);
  const percentage = ((value - min) / safeRange) * 100;

  const displayValue = formatValue
    ? formatValue(value)
    : `${prefix || ''}${value.toLocaleString()}${suffix || ''}`;

  /**
   * Convert a clientX coordinate into a clamped, stepped value
   * and invoke the onChange callback safely.
   */
  const handleTrackInteraction = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const ratio = x / rect.width;
      const rawValue = min + ratio * (max - min);
      const steppedValue = Math.round(rawValue / step) * step;
      const clampedValue = Math.max(min, Math.min(max, steppedValue));

      // Only call onChange if the value actually changes
      if (clampedValue !== value) {
        try {
          onChange(clampedValue);
        } catch (err) {
          console.error('PropertySlider onChange error:', err);
        }
      }
    },
    [min, max, step, onChange, value],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault(); // Prevent text selection while dragging
      setIsDragging(true);
      handleTrackInteraction(e.clientX);
    },
    [handleTrackInteraction],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      setIsDragging(true);
      if (e.touches[0]) {
        handleTrackInteraction(e.touches[0].clientX);
      }
    },
    [handleTrackInteraction],
  );

  // Global listeners for move / end events while dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleTrackInteraction(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) {
        e.preventDefault(); // Prevent scrolling while dragging
        handleTrackInteraction(e.touches[0].clientX);
      }
    };

    const handleEnd = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, handleTrackInteraction]);

  const handleDecrement = () => {
    const newVal = Math.max(min, value - step);
    if (newVal !== value) onChange(newVal);
  };

  const handleIncrement = () => {
    const newVal = Math.min(max, value + step);
    if (newVal !== value) onChange(newVal);
  };

  return (
    <div className="mb-7">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#882629]/10">
            <i className={`${icon} text-[#882629] text-base`}></i>
          </div>
          <span className="text-sm font-semibold text-[#505A5A]">{label}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDecrement}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-[#505A5A] hover:bg-gray-100 transition-colors cursor-pointer select-none"
            aria-label={`Decrease ${label}`}
          >
            <i className="ri-subtract-line text-sm"></i>
          </button>

          <span
            className={`text-base font-bold text-[#882629] min-w-[80px] text-center transition-transform ${
              isDragging ? 'scale-110' : ''
            }`}
          >
            {displayValue}
          </span>

          <button
            onClick={handleIncrement}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-[#505A5A] hover:bg-gray-100 transition-colors cursor-pointer select-none"
            aria-label={`Increase ${label}`}
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="relative h-10 flex items-center cursor-pointer select-none touch-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="absolute left-0 right-0 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#882629] to-[#B8393D] rounded-full transition-all duration-75"
            style={{ width: `${percentage}%` }}
          />
        </div>

        <div
          className={`absolute w-6 h-6 bg-white border-[3px] border-[#882629] rounded-full shadow-md transition-shadow duration-150 -translate-x-1/2 ${
            isDragging
              ? 'shadow-lg scale-110 border-[#6E1F22]'
              : 'hover:shadow-lg'
          }`}
          style={{ left: `${percentage}%` }}
        />
      </div>

      <div className="flex justify-between mt-1">
        <span className="text-xs text-[#505A5A]/50">
          {prefix || ''}
          {min.toLocaleString()}
          {suffix || ''}
        </span>
        <span className="text-xs text-[#505A5A]/50">
          {prefix || ''}
          {max.toLocaleString()}
          {suffix || ''}
          +
        </span>
      </div>
    </div>
  );
}
